// @ts-nocheck
import { computed, toRefs, nextTick, watch, toValue, onMounted } from "vue";
import { pick, isArray } from "lodash-es";
import { ElSelect, useNamespace, UPDATE_MODEL_EVENT, ElTable, ElPagination } from "element-plus";
import type { Ref } from "vue";
import { useGlobalConfig } from "../../config-provider/src/hooks/use-global-config";
import { onKeyStroke } from "@vueuse/core";

export const useSelect = (
  props,
  { attrs, emit },
  {
    key,
    filter,
    select,
    currentPage,
    pagination,
    table
  }: {
    select: Ref<InstanceType<typeof ElSelect> | undefined>;
    table: Ref<InstanceType<typeof ElTable> | undefined>;
    pagination: Ref<InstanceType<typeof ElPagination> | undefined>;
    key: Ref<string>;
    filter: (query: string) => void;
    currentPage: Ref<number>;
  }
) => {
  const ns = useNamespace("table-select");
  const config = useGlobalConfig();

  watch(currentPage, (value, old) => {
    if (value !== old)
      props?.remoteMethod?.({
        query: select.value.query,
        [config.value.currentPageKey]: value,
        [config.value.pageSizeKey]: pagination.value.pageSize
      });
  });

  watch(
    () => [props.modelValue, props.data],
    ([_modelValue]) => {
      const modelValue = toValue(_modelValue);
      if (modelValue && !props.multiple) {
        table.value?.store.setCurrentRowKey(modelValue);
      }
      if (props.multiple) {
        table.value?.clearSelection();
        if (isArray(modelValue) && modelValue.length > 0) {
          nextTick(() => {
            for (const item of table.value?.store.states.data.value) {
              if (modelValue.includes(item[key.value])) {
                table.value?.toggleRowSelection(item, true);
              }
            }
          });
        }
      }
    },
    { immediate: true, deep: true }
  );

  const navigateOptions = async () => {
    await nextTick();
    const rowEls = (table.value?.$refs.tableBody as HTMLTableElement).querySelectorAll(
      ".el-table__row"
    );
    if (rowEls.length > 0 && rowEls[select.value.hoverIndex]) {
      rowEls.forEach((item) => item.classList.remove("hover-row"));
      rowEls[select.value.hoverIndex].classList.add("hover-row");
    }
  };

  onMounted(() => {
    onKeyStroke(
      ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"],
      (e) => {
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          navigateOptions();
        }
        if (e.key === "ArrowLeft") {
          if (pagination.value.currentPage <= 1) {
            currentPage.value = pagination.value.pageCount;
          } else {
            currentPage.value = pagination.value.currentPage - 1;
          }
        }
        if (e.key === "ArrowRight") {
          if (pagination.value.currentPage >= pagination.value.pageCount) {
            currentPage.value = 1;
          } else {
            currentPage.value = pagination.value.currentPage + 1;
          }
        }
      },
      { target: select.value?.input || select.value.reference.input }
    );
  });

  const result = {
    ...pick(toRefs(props), Object.keys(ElSelect.props)),
    ...attrs,
    loading: false,
    // attrs is not reactive, when v-model binding source changes,
    // this listener is still old, see the bug(or test 'v-model source change'):
    // https://github.com/element-plus/element-plus/issues/14204
    "onUpdate:modelValue": (value) => emit(UPDATE_MODEL_EVENT, value),
    valueKey: key,
    popperClass: computed(() => {
      const classes = [ns.e("popper")];
      if (props.popperClass) classes.push(props.popperClass);
      return classes.join(" ");
    }),
    filterMethod: (keyword = "") => {
      if (props.filterMethod) props.filterMethod(keyword);

      nextTick(() => {
        // let tree node expand only, same with tree filter
        filter(keyword);
      });
    },
    // clear filter text when visible change
    onVisibleChange: (visible: boolean) => {
      attrs.onVisibleChange?.(visible);
      if (props.filterable && visible) {
        result.filterMethod();
      }
      if (visible) {
        navigateOptions();
      }
    },
    remoteMethod: (query: string) => {
      if (select.value?.visible)
        props?.remoteMethod?.({
          query,
          [config.value.currentPageKey]: pagination.value?.currentPage,
          [config.value.pageSizeKey]: pagination.value?.pageSize
        });
    }
  };

  return result;
};
